// frontend/src/SupplierManagement.tsx - Enhanced with ABR Integration

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';
import { extractErrorMessage, logError } from './utils/errorUtils';

interface Supplier {
id: number;
name: string;
abn_acn: string;
// ABR lookup fields
entity_name?: string;
abn_status?: string;
entity_type?: string;
gst_status?: string;
main_location?: string;
abr_lookup_date?: string;
is_manual_entry?: boolean;
}

interface SupplierFormData {
name: string;
abn_acn: string;
// ABR lookup fields
entity_name?: string;
abn_status?: string;
entity_type?: string;
gst_status?: string;
main_location?: string;
is_manual_entry?: boolean;
}

interface ABRLookupResponse {
success: boolean;
data?: {
  entity_name: string;
  abn_status: string;
  entity_type: string;
  gst_status: string;
  main_location: string;
  abn: string;
};
error?: string;
}

interface SupplierManagementProps {
processId: number;
processName: string;
onBack: () => void;
}

const styles = {
  mainContainer: {
    minHeight: '100vh',
    background: 'var(--background-brand-gradient)',
    fontFamily: 'var(--font-family-primary)',
    color: 'var(--color-text-primary)',
    padding: 'var(--spacing-lg)'
  },
  contentWrapper: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  pageTitle: {
    fontSize: 'var(--font-size-headline-large)',
    fontWeight: 'var(--font-weight-headline)',
    color: 'var(--color-text-on-gradient)',
    marginBottom: 'var(--spacing-xl)',
    textAlign: 'center' as 'center'
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 'var(--border-radius-lg)',
    padding: 'var(--spacing-xl)',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 'var(--spacing-lg)'
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0
  },
  button: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    borderRadius: 'var(--border-radius-md)',
    fontWeight: 'var(--font-weight-medium)',
    fontSize: 'var(--font-size-sm)',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)'
  },
  buttonPrimary: {
    background: 'var(--color-primary)',
    color: 'white'
  },
  buttonSecondary: {
    background: 'var(--color-surface-variant)',
    color: 'var(--color-text-primary)'
  },
  buttonDestructive: {
    background: 'var(--color-error)',
    color: 'white'
  },
  formContainer: {
    marginBottom: 'var(--spacing-xl)',
    padding: 'var(--spacing-lg)',
    background: 'rgba(255, 255, 255, 0.5)',
    borderRadius: 'var(--border-radius-md)',
    border: '1px solid var(--color-surface-variant)'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-lg)'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as 'column'
  },
  formLabel: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-xs)'
  },
  input: {
    padding: 'var(--spacing-sm)',
    borderRadius: 'var(--border-radius-sm)',
    border: '1px solid var(--color-surface-variant)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-family-primary)',
    outline: 'none',
    transition: 'border-color 0.2s ease'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as 'collapse',
    marginTop: 'var(--spacing-md)'
  },
  tableHeader: {
    background: 'var(--color-surface-variant)',
    padding: 'var(--spacing-sm)',
    textAlign: 'left' as 'left',
    fontWeight: 'var(--font-weight-semibold)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    borderBottom: '1px solid var(--color-surface-variant)'
  },
  tableCell: {
    padding: 'var(--spacing-sm)',
    borderBottom: '1px solid var(--color-surface-variant)',
    fontSize: 'var(--font-size-sm)'
  },
  emptyState: {
    textAlign: 'center' as 'center',
    padding: 'var(--spacing-xl)',
    border: '1px dashed var(--color-surface-variant)',
    borderRadius: 'var(--border-radius-md)',
    color: 'var(--color-text-secondary)'
  },
  footer: {
    marginTop: 'var(--spacing-xl)',
    borderTop: '1px solid var(--color-surface-variant)',
    paddingTop: 'var(--spacing-lg)',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  alert: {
    padding: 'var(--spacing-sm)',
    borderRadius: 'var(--border-radius-md)',
    marginBottom: 'var(--spacing-md)',
    background: 'var(--color-error-surface)',
    color: 'var(--color-error)',
    border: '1px solid var(--color-error)'
  },
  modal: {
    position: 'fixed' as 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002
  },
  modalContent: {
    background: 'white',
    padding: 'var(--spacing-xl)',
    borderRadius: 'var(--border-radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    maxWidth: '400px',
    textAlign: 'center' as 'center'
  },
  modalTitle: {
    marginTop: 0,
    marginBottom: 'var(--spacing-md)',
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)'
  },
  modalActions: {
    display: 'flex',
    gap: 'var(--spacing-md)',
    justifyContent: 'center',
    marginTop: 'var(--spacing-lg)'
  }
};

const ConfirmationModal = ({ onConfirm, onCancel, message }: { onConfirm: () => void, onCancel: () => void, message: string }) => (
    <div style={styles.modal}>
        <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Are you sure?</h3>
            <p>{message}</p>
            <div style={styles.modalActions}>
                <button onClick={onCancel} style={{...styles.button, ...styles.buttonSecondary}}>Cancel</button>
                <button onClick={onConfirm} style={{...styles.button, ...styles.buttonDestructive}}>Confirm</button>
            </div>
        </div>
    </div>
);


const SupplierManagement: React.FC<SupplierManagementProps> = ({ processId, processName, onBack }) => {
const [suppliers, setSuppliers] = useState<Supplier[]>([]);
const [isLoading, setIsLoading] = useState(true);
const [error, setError] = useState<string | null>(null);
const [isAdding, setIsAdding] = useState(false);
const [newSupplier, setNewSupplier] = useState<SupplierFormData>({
    name: '',
    abn_acn: '',
    is_manual_entry: false
});
const [confirmAction, setConfirmAction] = useState<{ action: () => void, message: string } | null>(null);
const [isLookingUpABN, setIsLookingUpABN] = useState(false);
const [lookupMessage, setLookupMessage] = useState<string | null>(null);


const fetchSuppliers = useCallback(async () => {
    setIsLoading(true);
    try {
    const token = Cookies.get('access_token');
    const response = await axios.get(`${API_URL}/api/processes/${processId}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    setSuppliers(response.data);
    setError(null);
    } catch (err: any) {
    logError("Fetching suppliers", err, { processId });
    setError(extractErrorMessage(err, 'Failed to load suppliers. Please try again.'));
    } finally {
    setIsLoading(false);
    }
}, [processId]);

useEffect(() => {
    fetchSuppliers();
}, [fetchSuppliers]);

const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSupplier(prev => ({ ...prev, [name]: value }));
    
    // Clear lookup message when user modifies fields
    if (lookupMessage) {
        setLookupMessage(null);
    }
};

const handleABNLookup = async () => {
    const abn = newSupplier.abn_acn.trim();
    if (!abn) {
        setLookupMessage('Please enter an ABN/ACN first');
        return;
    }

    // Basic ABN format validation (11 digits) or ACN (9 digits)
    const cleanABN = abn.replace(/\s/g, '');
    if (!/^\d{9}$|^\d{11}$/.test(cleanABN)) {
        setLookupMessage('Invalid format. ABN must be 11 digits, ACN must be 9 digits');
        return;
    }

    setIsLookingUpABN(true);
    setLookupMessage(null);
    setError(null);

    try {
        const token = Cookies.get('access_token');
        const endpoint = cleanABN.length === 11 ? 'lookup-abn' : 'lookup-acn';
        
        const response = await axios.post(
            `${API_URL}/api/abr/${endpoint}`,
            { abn: cleanABN },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        const result: ABRLookupResponse = response.data;

        if (result.success && result.data) {
            // Auto-populate form with ABR data
            setNewSupplier(prev => ({
                ...prev,
                name: result.data!.entity_name,
                abn_acn: cleanABN,
                entity_name: result.data!.entity_name,
                abn_status: result.data!.abn_status,
                entity_type: result.data!.entity_type,
                gst_status: result.data!.gst_status,
                main_location: result.data!.main_location,
                is_manual_entry: false
            }));
            
            setLookupMessage(`✅ Found: ${result.data.entity_name} (${result.data.abn_status})`);
        } else {
            setLookupMessage(`❌ ${result.error || 'ABN/ACN not found'}`);
            // Mark as manual entry if lookup fails
            setNewSupplier(prev => ({ ...prev, is_manual_entry: true }));
        }
    } catch (err: any) {
        console.error('ABN lookup error:', err);
        setLookupMessage('❌ Unable to lookup ABN. You can continue with manual entry.');
        setNewSupplier(prev => ({ ...prev, is_manual_entry: true }));
    } finally {
        setIsLookingUpABN(false);
    }
};

const toggleManualEntry = () => {
    setNewSupplier(prev => ({
        ...prev,
        is_manual_entry: !prev.is_manual_entry,
        // Clear ABR fields if switching to manual entry
        ...(!prev.is_manual_entry ? {
            entity_name: undefined,
            abn_status: undefined,
            entity_type: undefined,
            gst_status: undefined,
            main_location: undefined
        } : {})
    }));
    setLookupMessage(null);
};

const handleAddSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
    const token = Cookies.get('access_token');
    
    // Transform data to match backend expectation
    const supplierData = {
        suppliers: [{
            name: newSupplier.name.trim(),
            abn_acn: newSupplier.abn_acn.trim(),
            entity_name: newSupplier.entity_name,
            abn_status: newSupplier.abn_status,
            entity_type: newSupplier.entity_type,
            gst_status: newSupplier.gst_status,
            main_location: newSupplier.main_location,
            is_manual_entry: newSupplier.is_manual_entry || false,
            abr_lookup_date: newSupplier.entity_name ? new Date().toISOString() : null
        }]
    };
    
    await axios.post(`${API_URL}/api/processes/${processId}/suppliers`, supplierData, {
        headers: { Authorization: `Bearer ${token}` },
    });
    
    // Reset form
    setNewSupplier({ 
        name: '', 
        abn_acn: '',
        is_manual_entry: false
    });
    setLookupMessage(null);
    setIsAdding(false);
    await fetchSuppliers();
    } catch (err: any) {
    logError("Adding supplier", err, { newSupplier, processId });
    setError(extractErrorMessage(err, 'Failed to add supplier. Please check the details and try again.'));
    } finally {
    setIsLoading(false);
    }
};

const handleDeleteSupplier = async (supplierId: number) => {
    setConfirmAction({
        action: async () => {
            setIsLoading(true);
            try {
                const token = Cookies.get('access_token');
                await axios.delete(`${API_URL}/api/processes/suppliers/${supplierId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                await fetchSuppliers();
            } catch (err) {
                console.error("Error deleting supplier:", err);
                setError('Failed to delete supplier. It might be associated with existing evaluations.');
            } finally {
                setIsLoading(false);
                setConfirmAction(null);
            }
        },
        message: 'Are you sure you want to delete this supplier? This action cannot be undone.'
    });
};

const handleActivateProcess = async () => {
    setConfirmAction({
        action: async () => {
            setIsLoading(true);
            try {
                const token = Cookies.get('access_token');
                await axios.patch(`${API_URL}/api/processes/${processId}/status`, 
                    { status: 'ACTIVE' },
                    { headers: { Authorization: `Bearer ${token}` }}
                );
                onBack(); // Go back to the dashboard
            } catch (err) {
                console.error("Error activating process:", err);
                setError('Failed to activate process. Please ensure at least one supplier has been added.');
            } finally {
                setIsLoading(false);
                setConfirmAction(null);
            }
        },
        message: 'This will activate the process and allow compliance checks and evaluator assignments to begin. Evaluation criteria will be locked. Proceed?'
    });
};

return (
    <div style={styles.mainContainer} className="density-compact">
        {confirmAction && (
            <ConfirmationModal 
                onConfirm={confirmAction.action}
                onCancel={() => setConfirmAction(null)}
                message={confirmAction.message}
            />
        )}
        <div style={styles.contentWrapper}>
            <h2 style={styles.pageTitle}>Manage Suppliers for: {processName}</h2>
            <div style={styles.card}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Supplier List</h3>
                    <button 
                        onClick={() => setIsAdding(!isAdding)} 
                        style={{...styles.button, ...(isAdding ? styles.buttonSecondary : styles.buttonPrimary)}}
                    >
                        {isAdding ? 'Cancel' : '＋ Add Supplier'}
                    </button>
                </div>

                {isAdding && (
                <form onSubmit={handleAddSupplier} style={styles.formContainer}>
                    {/* Manual Entry Toggle */}
                    <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input 
                                type="checkbox" 
                                checked={newSupplier.is_manual_entry || false}
                                onChange={toggleManualEntry}
                            />
                            <span>Manual entry (international supplier or no ABN/ACN)</span>
                        </label>
                    </div>

                    <div style={styles.formGrid}>
                        <div style={styles.formGroup}>
                            <label htmlFor="abn_acn" style={styles.formLabel}>
                                {newSupplier.is_manual_entry ? 'Registration Number (Optional)' : 'ABN/ACN'}
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input 
                                    type="text" 
                                    id="abn_acn" 
                                    name="abn_acn" 
                                    value={newSupplier.abn_acn} 
                                    onChange={handleFormChange} 
                                    style={{...styles.input, flex: 1}} 
                                    placeholder={newSupplier.is_manual_entry ? "Optional registration number" : "e.g., 12 345 678 901"}
                                    required={!newSupplier.is_manual_entry}
                                    disabled={isLookingUpABN}
                                />
                                {!newSupplier.is_manual_entry && (
                                    <button 
                                        type="button"
                                        onClick={handleABNLookup}
                                        disabled={isLookingUpABN || !newSupplier.abn_acn.trim()}
                                        style={{
                                            ...styles.button, 
                                            ...styles.buttonSecondary,
                                            whiteSpace: 'nowrap',
                                            minWidth: '120px'
                                        }}
                                    >
                                        {isLookingUpABN ? 'Looking up...' : '🔍 Lookup'}
                                    </button>
                                )}
                            </div>
                            {lookupMessage && (
                                <div style={{ 
                                    marginTop: '0.5rem', 
                                    padding: '0.5rem', 
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    background: lookupMessage.startsWith('✅') ? '#d4edda' : '#f8d7da',
                                    color: lookupMessage.startsWith('✅') ? '#155724' : '#721c24',
                                    border: `1px solid ${lookupMessage.startsWith('✅') ? '#c3e6cb' : '#f5c6cb'}`
                                }}>
                                    {lookupMessage}
                                </div>
                            )}
                        </div>
                        <div style={styles.formGroup}>
                            <label htmlFor="name" style={styles.formLabel}>
                                Supplier Name {newSupplier.entity_name && !newSupplier.is_manual_entry && '(from ABR)'}
                            </label>
                            <input 
                                type="text" 
                                id="name" 
                                name="name" 
                                value={newSupplier.name} 
                                onChange={handleFormChange} 
                                style={styles.input} 
                                required 
                                placeholder={newSupplier.is_manual_entry ? "Enter supplier name" : "Will be auto-filled from ABN lookup"}
                            />
                        </div>
                    </div>

                    {/* ABR Details Display */}
                    {newSupplier.entity_name && !newSupplier.is_manual_entry && (
                        <div style={{ 
                            marginTop: '1rem', 
                            padding: '1rem', 
                            background: '#e8f5e8', 
                            borderRadius: '8px',
                            border: '1px solid #c3e6cb'
                        }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#155724' }}>
                                ABR Information Retrieved:
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', fontSize: '0.9rem' }}>
                                <div><strong>Entity Status:</strong> {newSupplier.abn_status}</div>
                                <div><strong>Entity Type:</strong> {newSupplier.entity_type}</div>
                                <div><strong>GST Status:</strong> {newSupplier.gst_status}</div>
                                <div><strong>Location:</strong> {newSupplier.main_location}</div>
                            </div>
                        </div>
                    )}

                    <button 
                        type="submit" 
                        style={{...styles.button, ...styles.buttonPrimary}} 
                        disabled={isLoading || isLookingUpABN}
                    >
                        {isLoading ? 'Saving...' : 'Save Supplier'}
                    </button>
                </form>
                )}

                {isLoading && !isAdding && <div>Loading...</div>}
                
                {error && <div style={styles.alert}>{error}</div>}

                {!isLoading && suppliers.length === 0 && !isAdding && (
                <div style={styles.emptyState}>
                    <p>No suppliers have been added to this process yet.</p>
                </div>
                )}

                {!isLoading && suppliers.length > 0 && (
                <div>
                    <table style={styles.table}>
                    <thead>
                        <tr>
                        <th style={styles.tableHeader}>Supplier Name</th>
                        <th style={styles.tableHeader}>ABN/ACN</th>
                        <th style={styles.tableHeader}>Entity Type</th>
                        <th style={styles.tableHeader}>Status</th>
                        <th style={styles.tableHeader}>GST</th>
                        <th style={styles.tableHeader}>Source</th>
                        <th style={styles.tableHeader}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {suppliers.map(supplier => (
                        <tr key={supplier.id}>
                            <td style={styles.tableCell}>
                                {supplier.entity_name || supplier.name}
                                {supplier.entity_name && supplier.entity_name !== supplier.name && (
                                    <div style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>
                                        Trading as: {supplier.name}
                                    </div>
                                )}
                            </td>
                            <td style={styles.tableCell}>
                                {supplier.abn_acn}
                                {supplier.main_location && (
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                        📍 {supplier.main_location}
                                    </div>
                                )}
                            </td>
                            <td style={styles.tableCell}>
                                {supplier.entity_type || '—'}
                            </td>
                            <td style={styles.tableCell}>
                                <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '0.8rem',
                                    backgroundColor: 
                                        supplier.abn_status === 'Active' ? '#d4edda' :
                                        supplier.abn_status === 'Cancelled' ? '#f8d7da' : '#e2e3e5',
                                    color:
                                        supplier.abn_status === 'Active' ? '#155724' :
                                        supplier.abn_status === 'Cancelled' ? '#721c24' : '#6c757d'
                                }}>
                                    {supplier.abn_status || 'Unknown'}
                                </span>
                            </td>
                            <td style={styles.tableCell}>
                                <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '0.8rem',
                                    backgroundColor: 
                                        supplier.gst_status === 'Registered' ? '#d1ecf1' :
                                        supplier.gst_status === 'Not Registered' ? '#fff3cd' : '#e2e3e5',
                                    color:
                                        supplier.gst_status === 'Registered' ? '#0c5460' :
                                        supplier.gst_status === 'Not Registered' ? '#856404' : '#6c757d'
                                }}>
                                    {supplier.gst_status || 'Unknown'}
                                </span>
                            </td>
                            <td style={styles.tableCell}>
                                <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '0.8rem',
                                    backgroundColor: supplier.is_manual_entry ? '#fff3cd' : '#d4edda',
                                    color: supplier.is_manual_entry ? '#856404' : '#155724'
                                }}>
                                    {supplier.is_manual_entry ? '✋ Manual' : '🔍 ABR'}
                                </span>
                                {supplier.abr_lookup_date && (
                                    <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '2px' }}>
                                        {new Date(supplier.abr_lookup_date).toLocaleDateString()}
                                    </div>
                                )}
                            </td>
                            <td style={styles.tableCell}>
                            <button 
                                onClick={() => handleDeleteSupplier(supplier.id)} 
                                style={{...styles.button, ...styles.buttonDestructive}} 
                                disabled={isLoading}
                            >
                                Delete
                            </button>
                            </td>
                        </tr>
                        ))}
                    </tbody>
                    </table>
                </div>
                )}
                
                <div style={styles.footer}>
                    <button 
                        onClick={handleActivateProcess} 
                        style={{...styles.button, ...styles.buttonPrimary}} 
                        disabled={isLoading || suppliers.length === 0}
                        title={suppliers.length === 0 ? "You must add at least one supplier to activate the process" : "Activate Process"}
                    >
                        Save and Activate Process
                    </button>
                </div>
            </div>
        </div>
    </div>
);
};

export default SupplierManagement;
